import { Suspense } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-premium';
import {
  Link,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { GET_LISTING_TRANSACTIONS_DATA } from '@modules/_shared/components/listing-overview/ListingTransactionsCard';
import { TransactionsList } from '@modules/_shared/components/transactions/TransactionsList';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import TransactionCreateDrawer from '@modules/transactions/TransactionCreateDrawer';
import TransactionDrawer from '@modules/transactions/TransactionDrawer';
import { useLocale } from '@shared/hooks/locale';

export const GET_LISTING_TRANSACTIONS_QUERY = gql(/* GraphQL */ `
  query GetListingTransactions(
    $listingId: uuid!
    $team_ids: [uuid!]!
    $includeValuations: Boolean! = false
  ) {
    property_transactions(
      where: { lot_id: { _eq: $listingId } }
      order_by: { created_at: desc }
    ) {
      ...TransactionsListRow
    }
  }
`);

const getTransactionLink = (
  transactionId: string,
  searchParams: URLSearchParams,
) => ({
  pathname: `./${transactionId}`,
  search: searchParams.toString(),
});

const getNewTransactionLink = (
  listingId: string,
  searchParams: URLSearchParams,
) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.set('selected-listing', listingId);

  return {
    pathname: 'new/listing',
    search: newSearchParams.toString(),
  };
};

export const ListingTransactions: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const { lotId } = useParams();
  const { me } = useAppData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const myTeamsIds = me?.teams?.map(team => team.team_id) ?? [];
  const { t } = useLocale();

  const { data, loading, error } = useQuery(GET_LISTING_TRANSACTIONS_QUERY, {
    skip: lotId == null,
    variables: {
      listingId: lotId ?? '',
      team_ids: myTeamsIds,
      // Valuations are not needed here
      // This is a share fragment with the transaction page
      // So just pass false here
      includeValuations: false,
    },
  });

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const rowCount = data?.property_transactions?.length ?? 0;

  return (
    <>
      <Stack
        spacing={2}
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Typography variant="h6">
          {t('Transactions ({{count}})', {
            count: rowCount,
          })}
        </Typography>

        {!readOnly && (
          <Button
            variant="contained"
            component={Link}
            to={getNewTransactionLink(lotId ?? '', searchParams)}
            disableElevation
          >
            {t('New')}
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          px: { sm: 0, md: 2 },
        }}
      >
        <Paper
          variant="outlined"
          sx={theme => ({
            overflow: 'hidden',
            [theme.breakpoints.down('md')]: { borderRadius: 0 },
          })}
        >
          <TransactionsList
            transactions={data?.property_transactions}
            loading={loading}
            extraDataGridProps={{
              sx: {
                borderColor: 'transparent',
                [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                  {
                    outline: 'none',
                  },
              },
              hideFooter: true,
              autoHeight: true,
              rowCount,
              rowHeight: 68,
              sortingMode: 'client',
              paginationMode: 'client',
              disableMultipleRowSelection: true,
              disableRowSelectionOnClick: true,
              onRowClick: ({ id }, e) => {
                if (e.metaKey || e.ctrlKey) {
                  window.open(
                    getTransactionLink(id as string, searchParams).pathname,
                    '_blank',
                  );
                  return;
                }

                navigate(getTransactionLink(id as string, searchParams));
              },
            }}
          />
        </Paper>
      </Box>
      <Suspense fallback={null}>
        <Routes>
          {!readOnly && (
            <Route
              path="new/*"
              element={
                <TransactionCreateDrawer
                  noTabs
                  refetchQueries={[
                    GET_LISTING_TRANSACTIONS_QUERY,
                    {
                      query: GET_LISTING_TRANSACTIONS_DATA,
                      variables: { id: lotId ?? '' },
                    },
                  ]}
                  freezeListingChoice
                  onCreate={id => {
                    navigate({
                      pathname: `./${id}/transaction`,
                      search: searchParams.toString(),
                    });
                  }}
                />
              }
            />
          )}
          <Route
            path=":transactionId/*"
            element={
              <TransactionDrawer
                readOnly={readOnly}
                refetchQueries={[
                  GET_LISTING_TRANSACTIONS_QUERY,
                  {
                    query: GET_LISTING_TRANSACTIONS_DATA,
                    variables: { id: lotId ?? '' },
                  },
                ]}
              />
            }
          />
        </Routes>
      </Suspense>
    </>
  );
};
